import React from "react";
import Image from "next/image";
import { getIconUrl } from "@/lib/directus";
import { cn } from "@/lib/utils";
import { LazyMotion, domAnimation, m } from "framer-motion";

interface HoverGalleryData {
  gallery: any[];
  sectionName: string;
  topTitle: string;
  title: string;
  bottomTitle: string;
  galleryTitle: string;
  galleryDescription: string;
}

interface HoverGalleryProps {
  data: HoverGalleryData;
}

const titleVariants = {
  hidden: { x: -60, opacity: 0 },
  visible: (i: number) => ({
    x: 0,
    opacity: 1,
    transition: { delay: 0.15 * i, duration: 0.6, ease: "easeOut" },
  }),
};

const imageVariants = {
  hidden: { y: 40, opacity: 0 },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.08 * i, duration: 0.5, ease: "easeOut" },
  }),
};

export default function HoverGallery({
  data: {
    gallery,
    sectionName,
    topTitle,
    title,
    bottomTitle,
    galleryTitle,
    galleryDescription,
  },
}: HoverGalleryProps) {
  const sectionId = sectionName.replace(/\s+/g, "-");

  return (
    <LazyMotion features={domAnimation}>
      <section
      id={sectionId?.replaceAll(/[^a-zA-Z0-9-_]/g, "-")}
      className="w-full bg-[#232b33] py-16 px-4 flex flex-col items-center"
    >
      <div className="max-w-4xl w-full text-center mb-12">
        <m.p
          className="text-special-h2 text-secondary-light tracking-widest uppercase mb-2"
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          custom={0}
        >
          {topTitle}
        </m.p>
        <m.h2
          className="font-serif text-h2-mobile md:text-h2-tablet desktop:text-h2-desktop text-accent-color-two-light mb-2"
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          custom={1}
        >
          {title}
        </m.h2>
        <m.p
          className="text-special-h2 text-secondary-light uppercase mb-6"
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          custom={2}
        >
          {bottomTitle}
        </m.p>
        <m.p
          className="text-white text-paragraph-large mb-6 desktop:mb-12"
          variants={titleVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          custom={3}
        >
          {galleryTitle}
        </m.p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 desktop:grid-cols-4 gap-6 desktop:gap-12 w-full max-w-6xl mb-8">
        {gallery.map((item, i) => (
          <m.div
            key={i}
            className="relative w-full overflow-hidden rounded group cursor-pointer shadow-lg"
            variants={imageVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            custom={i}
          >
            <Image
              src={getIconUrl(item.item.image)}
              alt={item.item.title}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105 aspect-[1.42] w-full !relative"
              sizes="(max-width: 768px) 50vw, 25vw"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-80 transition-all duration-300 flex items-center justify-center">
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-80 transition-all duration-300 flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 text-white text-xl font-serif text-center px-4 transition-opacity duration-300">
                  {item.item.title}
                </span>
              </div>
            </div>
          </m.div>
        ))}
      </div>
      <p className="text-white text-paragraph-large max-w-6xl text-center mt-8">
        {galleryDescription}
      </p>
    </section>
    </LazyMotion>
  );
}
